import { Location } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiCacheService } from '../cache-service/api-cache-service';
import { ToastService } from '../toast/toast-service';

@Injectable({
  providedIn: 'root'
})
export class ModulesService {
  private http = inject(HttpClient);
  private location = inject(Location);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private cache = inject(ApiCacheService);

  private baseURL: string = environment.HTTP;

  private modules = signal<any[]>([]);
  private selectedModule = signal<any | null>(null);
  private globalSearchValue = signal<string>('');

  private previousUrl = '';

  apiGetMenuModulesByUserCode = `${this.baseURL}/Permission/GetMenuModulesByUserCode`;
  apiGetFavourites = `${this.baseURL}/Permission/GetFavouriteModules`;

  getUserCode() {
    const user = localStorage.getItem('user');
    if (!user) return null;

    return JSON.parse(user).userCode;
  }

  //#region Modules
  getMenuModulesByUserCode() {
    const userCode = this.getUserCode();
    return firstValueFrom(this.http.get(this.apiGetMenuModulesByUserCode, { params: { userCode: userCode } }));
  }

  getFavouriteModules() {
    return this.cache.get(`apiGetFavourites`, () => this.http.get<any>(this.apiGetFavourites));
  }

  // loadModules() {
  //   this.getMenuModulesByUserCode().subscribe({
  //     next: (res: any) => {
  //       this.modules.set(res.data);
  //     },
  //   });
  // }

  async loadModules(forceReload: boolean = false) {
    if (this.modules().length > 0 && !forceReload) return;

    try {
      const result: any = await this.getMenuModulesByUserCode();
      this.modules.set(result.data);
    } catch (error) {
      console.error('Failed to fetch modules', error);
      this.modules.set([]);
    }
  }

  getModules() {
    return this.modules;
  }

  setSelectedModule(module: any) {
    this.selectedModule.set(module);
    localStorage.setItem('selectedModule', JSON.stringify(module));
  }

  getSelectedModule() {
    if (!this.selectedModule()) {
      const moduleStr = localStorage.getItem('selectedModule');
      if (moduleStr) {
        this.selectedModule.set(JSON.parse(moduleStr));
      }
    }
    return this.selectedModule;
  }

  clearModules() {
    this.modules.set([]);
  }

  clearSelectedModule() {
    this.selectedModule.set(null);
    localStorage.removeItem('selectedModule');
  }
  //#endregion Modules

  //#region Breadcrumbs
  getBreadcrumbsByRoute(route: string): { breadcrumbs: any[]; category: any | null } {
    const modules = this.modules();

    if (!Array.isArray(modules) || !modules.length) {
      return { breadcrumbs: [], category: null };
    }

    // Special case for global search
    if (route === 'search') {
      return { breadcrumbs: [{ name: 'SEARCH', route: 'search' }], category: null };
    }

    // Find the complete hierarchy for the current route
    const foundPath = this.findModulePath(modules, route);

    if (!foundPath?.length) return { breadcrumbs: [], category: null };
    const breadcrumbs = this.buildBreadcrumbs(foundPath);

    // CATEGORY is now explicitly identified by moduleType
    const category = [...foundPath].reverse().find((module) => module.moduleType === 'CATEGORY') || null;
    return { breadcrumbs, category };
  }

  private findModulePath(items: any[], route: string, currentPath: any[] = []): any[] | null {
    for (const item of items) {
      const path = [...currentPath, item];

      if (item.moduleRoute && (route === item.moduleRoute || route.startsWith(`${item.moduleRoute}/`))) {
        return path;
      }

      // Continue searching children
      if (Array.isArray(item.submodules) && item.submodules.length) {
        const result = this.findModulePath(item.submodules, route, path);

        if (result) return result;
      }
    }

    return null;
  }

  // Build breadcrumb objects from path
  private buildBreadcrumbs(path: any[]): any[] {
    const breadcrumbs: any[] = [];

    path.forEach((module) => {
      if (module.moduleLevel === 0) {
        breadcrumbs.push({
          name: module.moduleName,
          route: module.moduleRoute ? `sub-navigation/${module.moduleRoute}` : ''
        });

        return;
      }

      if (module.moduleType === 'MAIN_MODULE' || module.moduleType === 'CATEGORY') return;

      if (module.moduleType === 'SUB_MODULE' || module.moduleType === 'APPROVAL_SUB_MODULE') {
        breadcrumbs.push({
          name: module.moduleName,
          route: module.moduleRoute
        });
      }
    });

    return breadcrumbs;
  }

  //#region Elastic/Global Search
  getGlobalSearchValue() {
    return this.globalSearchValue();
  }

  setGlobalSearchValue(searchValue: string) {
    this.globalSearchValue.set(searchValue);
  }

  closeGlobalSearch() {
    if (this.router.url !== '/search') {
      return;
    }
    if (this.previousUrl.toLowerCase().includes('/login')) {
      this.router.navigate(['/dashboard']);
      return;
    }

    this.location.back();
  }

  baseElasticSearch = 'ElasticSearch';
  apiElasticSearchGetModules = `${this.baseURL}/${this.baseElasticSearch}/Get/Modules`;
  apiElasticSearchGetProfiles = `${this.baseURL}/${this.baseElasticSearch}/Get/Profiles`;

  getElasticSearchModules(searchValue: string) {
    const userCode = this.getUserCode();

    const params = new HttpParams()
      .append('value', searchValue)
      .append('size', 150)
      .append('userCode', userCode)
      .append('isGetFavourite', false);

    return this.cache.get(`apiElasticSearchGetModules=v${searchValue}u${userCode}`, () =>
      this.http.get(this.apiElasticSearchGetModules, { params: params })
    );
  }

  getElasticSearchProfiles(searchValue: string) {
    const params = new HttpParams().append('value', searchValue).append('size', 13); //search 13, show more button will appear if records > 12

    return this.cache.get(`apiElasticSearchGetProfiles=${searchValue}`, () =>
      this.http.get(this.apiElasticSearchGetProfiles, { params: params })
    );
  }
  //#endregion
}
